import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { LOGO_URL } from "../../lib/constants";
import { toast } from "sonner";
import { Eye, EyeOff, AtSign, Lock, ShieldCheck, Home } from "lucide-react";

/* Staff Login — same split-card design as the customer Login/Signup page:
   fan-art panel on the left with a LOGIN tab on the seam, badge + labelled
   fields + pill button on the right. */
function AuthField({ icon: Icon, label, children }) {
  return (
    <label className="block text-left">
      <span className="flex items-center gap-1.5 text-[13px] font-bold text-ink mb-1.5">
        <Icon size={13} className="text-ink" /> {label}
      </span>
      {children}
    </label>
  );
}

export default function StaffLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  // If a staff member is already logged in, skip the login screen.
  useEffect(() => {
    if (user && user.role !== "customer") {
      nav("/staff", { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role === "customer") { toast.error("Customer account — please use main login"); return; }
      toast.success(`Welcome, ${u.name}`);
      nav("/staff");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid credentials");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-pearl flex items-center justify-center px-4 py-10">
      <div className="auth-card w-full max-w-4xl grid md:grid-cols-[42%_58%] min-h-[540px]">

        {/* ============ Left: decorative art panel with seam tab ============ */}
        <div className="auth-art hidden md:block">
          <div className="auth-fan" style={{ width: 420, height: 420, top: -140, left: -160 }} />
          <div className="auth-fan slow" style={{ width: 360, height: 360, top: 160, left: -60, opacity: 0.8 }} />
          <div className="auth-fan" style={{ width: 380, height: 380, bottom: -150, left: -130, animationDuration: "110s" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/25" />

          <img src={LOGO_URL} alt="" className="absolute top-8 left-8 h-10 z-10" />

          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-10">
            <span className="auth-tab active pl-7 pr-8 py-3 text-sm">STAFF LOGIN</span>
          </div>
        </div>

        {/* Mobile header */}
        <div className="flex md:hidden bg-maroon-dark items-center justify-center py-3">
          <span className="text-pearl text-sm font-bold tracking-widest">STAFF LOGIN</span>
        </div>

        {/* ============ Right: the form ============ */}
        <div className="bg-white px-6 sm:px-12 lg:px-16 py-10 flex flex-col justify-center">
          <div className="flex flex-col items-center mb-7">
            <div className="auth-badge">
              <ShieldCheck size={30} />
            </div>
            <h1 className="font-display font-extrabold tracking-[0.08em] text-2xl text-ink mt-4">LOGIN</h1>
            <p className="text-xs text-ash mt-1">Swayambhu Gold — administrative panel</p>
          </div>

          <form onSubmit={submit} className="space-y-4 max-w-sm w-full mx-auto">
            <AuthField icon={AtSign} label="Email Address">
              <input data-testid="staff-email" type="email" placeholder="name@example.com" className="auth-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </AuthField>

            <AuthField icon={Lock} label="Password">
              <div className="relative">
                <input
                  data-testid="staff-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="auth-input pr-11"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ash hover:text-maroon transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </AuthField>

            <div className="pt-2 text-center">
              <button type="submit" disabled={busy} className="auth-submit text-sm" data-testid="staff-login-btn">
                {busy ? "…" : "LOGIN"}
              </button>
            </div>
          </form>

          <div className="mt-8 text-center text-xs text-ash space-y-2">
            <div>
              Customer? <Link to="/login" className="text-maroon underline" data-testid="customer-login-link">Login here</Link>
            </div>
            <div>
              <Link to="/" className="inline-flex items-center gap-1 text-maroon hover:text-gold" data-testid="staff-login-home">
                <Home size={12}/> Back to website
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
